/** Student row for the management {@link DataTable}. */
import * as React from 'react';
import { User, GraduationCap, Baby, Building2, Heart } from 'lucide-react';
import { Button } from '@/components/forms/Button';
import { LabelBadge } from '@/components/common/LabelBadge';
import { TableRow } from '@/components/common/data-table';
import type { Student } from '@/types/inperson/student.types';
import { formatDeactivationDate } from '@/utils/common-utils';
import {
  deriveStudentDisplay,
  formatStudentEstablishmentNames,
  formatStudentEntryDate,
  formatStudentPresenceRate,
  getStudentDeactivationDate,
  getStudentDisplayName,
  getStudentExitReasonLabel,
  type StudentPageActions,
} from './student-table-utils';

/** Props for {@link StudentRow}. */
interface StudentRowProps {
  student: Student;
  onView?: (id: string) => void;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  pageActions?: StudentPageActions;
}

/** Student row for the management {@link DataTable}. */
export const StudentRow: React.FC<StudentRowProps> = ({
  student,
  onView,
  onEdit,
  onDelete,
  pageActions,
}) => {
  const canEdit = pageActions?.canEditRow ?? true;
  const canDelete = pageActions?.canDeleteRow ?? true;
  const { statusConfig, StatusIcon, levelLabel, isMinor, hasSpecialNeeds, genderConfig } =
    deriveStudentDisplay(student);

  function renderGenderBadge(): React.ReactNode {
    if (!genderConfig) return null;
    return (
      <LabelBadge
        label={genderConfig.label}
        icon={genderConfig.icon}
        variant="outlined"
        color={genderConfig.color}
        size="sm"
      />
    );
  }

  return (
    <TableRow>
      <td>
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-[color-mix(in_srgb,var(--color-1-nuance-1)_10%,transparent)]">
            <User className="w-4 h-4 text-[var(--color-1-nuance-1)]" />
          </div>
          <div className="flex flex-col">
            <span className="font-medium text-foreground">{getStudentDisplayName(student)}</span>
          </div>
        </div>
      </td>

      <td>{renderGenderBadge()}</td>

      <td>
        <LabelBadge
          label={levelLabel ?? 'N/R'}
          icon={GraduationCap}
          variant="ghost"
          color="var(--color-2-nuance-1)"
          size="sm"
        />
      </td>

      <td>
        <LabelBadge
          label={statusConfig.label}
          icon={StatusIcon}
          variant={statusConfig.badgeVariant}
          color={statusConfig.badgeColor}
          size="md"
        />
      </td>

      <td>
        <div className="flex flex-col items-center gap-2 justify-center">
          {isMinor && (
            <LabelBadge
              label="Mineur"
              icon={Baby}
              variant="ghost"
              color="var(--color-1-nuance-1)"
              size="sm"
            />
          )}
          {hasSpecialNeeds && (
            <LabelBadge
              label="Besoins spécifiques"
              icon={Heart}
              variant="ghost"
              color="var(--color-3-nuance-1)"
              size="sm"
            />
          )}
          {!isMinor && !hasSpecialNeeds && <span className="text-sm text-muted-foreground">-</span>}
        </div>
      </td>

      <td>
        <div className="flex items-center gap-2">
          <Building2 className="w-4 h-4" style={{ color: 'var(--color-1-nuance-1)' }} />
          <span className="text-sm text-foreground">
            {formatStudentEstablishmentNames(student)}
          </span>
        </div>
      </td>

      <td>
        <span className="text-sm text-foreground">{formatStudentEntryDate(student.createdAt)}</span>
      </td>

      <td>
        <span className="text-sm text-foreground">
          {formatDeactivationDate(getStudentDeactivationDate(student))}
        </span>
      </td>

      <td>
        <span className="text-sm text-foreground">{getStudentExitReasonLabel(student)}</span>
      </td>

      <td>
        <span className="text-sm text-foreground">{formatStudentPresenceRate(student)}</span>
      </td>

      <td className="text-center">
        <div className="flex items-center justify-center gap-1">
          <Button preset="row-view" onClick={() => onView?.(student.id)} />

          {canEdit && <Button preset="row-edit" onClick={() => onEdit?.(student.id)} />}

          {canDelete && <Button preset="row-delete" onClick={() => onDelete?.(student.id)} />}
        </div>
      </td>
    </TableRow>
  );
};
